import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ChevronDown, Phone, Calendar } from 'lucide-react';
import { companyInfo, services } from '../data/mock';
import { Link } from 'react-router-dom';

const HeroSection = () => {
  return (
    <section className="hero-section relative min-h-screen bg-black overflow-hidden pt-36 lg:pt-32">
      {/* Animated Background Lines */}
      <div className="absolute inset-0 overflow-hidden">
        <svg className="absolute right-0 top-0 h-full w-1/2 opacity-30" viewBox="0 0 400 800" fill="none">
          <motion.g
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            {[...Array(8)].map((_, i) => (
              <path
                key={i}
                d={`M${200 + i * 30} 0 L${350 + i * 30} 200 L${200 + i * 30} 400 L${350 + i * 30} 600 L${200 + i * 30} 800`}
                stroke="#30b4e8"
                strokeWidth="2"
                fill="none"
                className="animate-pulse"
                style={{ animationDelay: `${i * 0.1}s` }}
              />
            ))}
          </motion.g>
        </svg>
      </div>

      <div className="container mx-auto px-4 lg:px-8 h-full flex flex-col justify-center">
        <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[calc(100vh-180px)] py-12">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="z-10"
          >
            <div className="mb-6">
              <motion.span
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
                className="service-button bg-cyan-500/20 text-cyan-400 border-cyan-500/50"
              >
                Las Vegas Marketing Agency
              </motion.span>
            </div>

            <h1 className="hero-heading text-white mb-6">
              <span className="block">Transform Your</span>
              <span className="block text-cyan-400">Digital Presence</span>
            </h1>

            <p className="body-large text-white/70 mb-8 max-w-lg">
              {companyInfo.tagline}. We build stunning websites, craft winning marketing strategies, and grow brands that stand out.
            </p>

            {/* Service Tags */}
            <div className="flex flex-wrap gap-3 mb-8">
              {services.map((service, index) => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                >
                  <Link
                    to={`/services/${service.slug}`}
                    className="service-button hover:scale-105 transition-transform"
                    style={{
                      backgroundColor: service.color === 'mid-blue' ? 'rgba(48, 180, 232, 0.2)' :
                        service.color === 'mid-purple' ? 'rgba(217, 135, 255, 0.2)' :
                        service.color === 'mid-orange' ? 'rgba(255, 150, 90, 0.2)' :
                        service.color === 'mid-green' ? 'rgba(16, 185, 129, 0.2)' :
                        'rgba(255, 132, 228, 0.2)',
                      color: service.color === 'mid-blue' ? '#30b4e8' :
                        service.color === 'mid-purple' ? '#d987ff' :
                        service.color === 'mid-orange' ? '#ff965a' :
                        service.color === 'mid-green' ? '#34d399' :
                        '#ff84e4',
                      borderColor: 'transparent'
                    }}
                  >
                    {service.shortTitle}
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 mb-6">
              <motion.a
                href="#contact"
                className="cta-button bg-cyan-500 text-black border-cyan-500 hover:bg-cyan-400 flex items-center gap-2 group"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Calendar size={16} />
                Schedule Free Consultation
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </motion.a>
              <motion.a
                href={`tel:${companyInfo.phone}`}
                className="cta-button bg-transparent text-white border-white/30 hover:border-cyan-400 hover:text-cyan-400 flex items-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Phone size={16} />
                Call Us Today
              </motion.a>
            </div>
          </motion.div>

          {/* Right Content - Brand Display */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="hidden lg:flex justify-end items-end h-full relative"
          >
            <div className="absolute bottom-20 right-0 text-right">
              <span className="brand-display text-cyan-400 opacity-20">
                INFINITY
              </span>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
        >
          <a href="#services" className="flex flex-col items-center text-white/50 hover:text-cyan-400 transition-colors">
            <span className="text-xs font-mono tracking-widest mb-2">SCROLL</span>
            <ChevronDown size={20} className="animate-bounce" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
